import { Descriptions, Grid, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
import { type MobilityModuleWithPermissionsSpecsFragment } from '../../../../api/fragments';
import { ContentRefinementSourceAction, ContentRefinementSourceKind } from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import MobilityModuleAction from './MobilityModuleAction';
import MobilityModuleDetailsInner from './MobilityModuleDetailsInner';
import MobilityModuleFooter from './MobilityModuleFooter';
import HeaderTabs from './shared';

export type MobilityModulePageProps = { module: MobilityModuleWithPermissionsSpecsFragment };

const MobilityModulePage = ({ module }: MobilityModulePageProps) => {
    const { t } = useTranslation(['mobilityModuleDetails']);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.MainDetails);

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const customerModuleInfo = useMemo(
        () =>
            `${module.customerModule.displayName} - ${module.kycPresets
                .map(kycPreset => kycPreset.displayName)
                .join(', ')}`,
        [module.customerModule.displayName, module.kycPresets]
    );

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: permissions.hasUpdatePermission,
            source: {
                kind: ContentRefinementSourceKind.Module,
                action: ContentRefinementSourceAction.Update,
                identifier: module.id,
            },
        }),
        [module.id, permissions.hasUpdatePermission]
    );

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('mobilityModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('mobilityModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>

            <Descriptions.Item label={t('mobilityModuleDetails:content.agreement')}>
                {module.agreementsModule.displayName}
            </Descriptions.Item>

            <Descriptions.Item label={t('mobilityModuleDetails:content.customer')}>
                {customerModuleInfo}
            </Descriptions.Item>

            <Descriptions.Item label={t('mobilityModuleDetails:content.vehicle')}>
                {module.vehicleModule.displayName}
            </Descriptions.Item>

            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <ConsolePageWithHeader
                content={content}
                extra={<MobilityModuleAction module={module} permissions={permissions} />}
                footer={[<MobilityModuleFooter />]}
                header={{
                    footer: (
                        <Tabs
                            activeKey={activeTab}
                            items={[
                                {
                                    label: t('mobilityModuleDetails:tabs.mainDetails.title'),
                                    key: HeaderTabs.MainDetails,
                                },
                                {
                                    label: t('mobilityModuleDetails:tabs.emailContent.title'),
                                    key: HeaderTabs.EmailContent,
                                },
                            ]}
                            onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}
                        />
                    ),
                }}
                onBack={() => navigate('/admin/system/modules')}
                title={screens.md ? t('mobilityModuleDetails:title', { name: module.displayName }) : module.displayName}
            >
                <MobilityModuleDetailsInner activeTab={activeTab} module={module} />
            </ConsolePageWithHeader>
        </GenericStateOnPageContextProvider>
    );
};

export default MobilityModulePage;
